// Library imports
import { RootState } from 'store';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import { Col, Radio, RadioChangeEvent, Row, Select, Tooltip, Typography } from 'antd';
// Local imports
import { days, operatorOptions, times } from '../constants';
import { DefaultOptionType } from 'antd/lib/select';
import { setSelectedIfElsesAction } from '../actions';
import SelectAnt from 'modules/Settings/Forms/Components/SelectAnt';
import { ReactComponent as Tick } from 'assets/img/icons/GreenDropdownTick.svg';
import { ControlNameEnum, ConditionEmails, ConditionEnum } from '../interfaces';

type IConditionControls = {
  id: string | number;
  value:
    | ConditionEnum.HAS_CLICKED
    | ConditionEnum.HAS_OPENED
    | ConditionEnum.CURRENT_DAY
    | ConditionEnum.CURRENT_TIME
    | ConditionEnum.HAS_NOT_OPENED
    | ConditionEnum.HAS_NOT_CLICKED;
  label:
    | ConditionEnum.HAS_CLICKED
    | ConditionEnum.HAS_OPENED
    | ConditionEnum.CURRENT_DAY
    | ConditionEnum.CURRENT_TIME
    | ConditionEnum.HAS_NOT_OPENED
    | ConditionEnum.HAS_NOT_CLICKED;
};

const { Option, OptGroup } = Select;
const { Paragraph } = Typography;

interface IConditionSelection {
  id: string;
  emails: ConditionEmails[];
  nodes: any;
  conditions?: any;
  condition?: any;
  conditionControls: IConditionControls[];
  conditionOperators?: any;
  setConditions: (id: any) => void;
  setConditionOperators: (val: any) => void;
  enableSaveButton: (value: boolean) => void;
  setIsOperatorChange?: (val: boolean) => void;
  index?: any;
  isAdditional?: boolean;
  removeCondition?: any;
  updateNode: any;
  waitRadioValue?: number;
}

const ConditionSelection: React.FC<IConditionSelection> = (props) => {
  // Props
  const {
    id,
    index,
    emails,
    conditionOperators,
    condition,
    conditions,
    setConditionOperators,
    setIsOperatorChange,
    enableSaveButton,
    conditionControls,
    removeCondition,
    updateNode,
    isAdditional,
    waitRadioValue,
  } = props;

  // Redux States
  const emailsLinks: any = useSelector((state: RootState) => state.automation.emailsLinks.data);
  const itemData = useSelector((state: RootState) => state.automation.workflow.flow[id]?.detail);
  const modal = useSelector((state: RootState) => state.automation.modals.addAction);
  const actionModalVisible: boolean = useSelector((state: RootState) => state.automation.modals.addAction.visible);

  // Local Variables
  const myTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  let timezones = [{ value: `My Timezone (${myTimezone})`, label: `My Timezone (${myTimezone})` }];
  let verbs = [{ value: 'Is', label: 'Is' }];

  // Local States
  const [selectedCondition, setSelectedCondition] = useState<IConditionControls>(conditionControls[0]);
  const [selectedTimezone, setSelectedTimezone] = useState(timezones[0]);
  const [selectedVerb, setSelectedVerb] = useState({ value: 'Is', label: 'Is' });
  const [selectedDay, setSelectedDay] = useState({ value: 'Select day', label: 'Select day' });
  const [selectedTime, setSelectedTime] = useState({ value: 'Select time', label: 'Select time' });
  const [selectedEmail, setSelectedEmail] = useState('');
  const [selectedEmailLinks, setSelectedEmailLinks] = useState<any>([]);
  const [selectedLink, setSelectedLink] = useState(null);
  const [selectedLinkType, setSelectedLinkType] = useState(null);

  // Component Logic
  const onChangeOperator = ({ target: { value } }: RadioChangeEvent) => {
    let operators = conditionOperators?.map((operator: any) => (operator = value));
    setConditionOperators(operators);
    if (setIsOperatorChange) setIsOperatorChange(false);
  };

  const onEmailChange = (value: any) => {
    let conditionObject = { ...conditions[index] };
    if (
      value === undefined ||
      (selectedEmail !== value &&
        (selectedCondition.value === ConditionEnum.HAS_CLICKED || selectedCondition.value === ConditionEnum.HAS_NOT_CLICKED))
    ) {
      setSelectedLink(null);
      setSelectedLinkType(null);
      setSelectedEmail(value);
      conditionObject.linkType = null;
      conditionObject.link = null;
    } else {
      setSelectedEmail(value);
    }
    if (value) {
      let email = emails.find((email) => value == email?.sp_uuid || email.emailId == parseInt(value));
      let condition = {
        ...conditionObject,
        emailId: email?.emailId,
        emailName: email?.title,
        emailType: email?.sp_uuid ? 'share profile' : 'email', // 10 is for shared email
        ...(email?.sp_uuid ? { sp_uuid: email?.sp_uuid } : { sp_uuid: '' }),
      };
      updateNode(condition, index);
      let links = emailsLinks[value];
      setSelectedEmailLinks(links);
    }
  };

  const onLinkChange = (value: any) => {
    let values = value?.split('#-#');
    let condition = {
      ...conditions[index],
    };
    // #-#
    if (value !== undefined) {
      setSelectedLink(values[1]);
      setSelectedLinkType(values[0]);
      condition = { ...condition, link: values[1], linkType: values[0] };
    } else {
      setSelectedLink(null);
      setSelectedLinkType(null);
      condition = { ...condition, link: null, linkType: null };
    }
    updateNode(condition, index);
  };

  const selectHandler = (value: any, option: any) => {
    setSelectedCondition(option);
    let condition = {
      ...conditions[index],
      controlName: option.value,
      controlId: option.id,
    };
    if ((option.id === 4 || option.id === 16) && !selectedLink) {
      if (selectedEmail) {
        let links = emailsLinks[selectedEmail];
        setSelectedEmailLinks(links);
      }
    } else if ((option.id === 3 || option.id === 15) && selectedEmail) {
      setSelectedLink(null);
      setSelectedLinkType(null);
    } else if (option.id === 12 || option.id === 13) {
      condition = { ...condition, verb: verbs[0].value, timezone: myTimezone };
    }
    if (!selectedEmail && condition.emailId) {
      condition.emailId = '';
      condition.emailName = '';
      condition.emailType = '';
      condition.sp_uuid = '';
    }
    updateNode(condition, index);
  };

  const selectTimezoneHandler = (val: any) => {
    const value = { value: val, label: val };
    if (selectedTimezone !== value) {
      setSelectedTimezone(value);
      let condition = { ...conditions[index] };
      condition = { ...condition, timezone: value.value.split('(')[1].split(')')[0] };
      updateNode(condition, index);
    }
  };
  const selectVerbHandler = (val: any) => {
    const value = { value: val, label: val };
    if (selectedVerb !== value) {
      setSelectedVerb(value);
      let condition = { ...conditions[index] };
      condition = { ...condition, verb: value.value };
      updateNode(condition, index);
    }
  };
  const selectDayHandler = (val: any) => {
    const value = { value: val, label: val };
    if (selectedDay !== value) {
      setSelectedDay(value);
      let condition = { ...conditions[index] };
      condition = { ...condition, day: value.value };
      updateNode(condition, index);
    }
  };
  const selectTimeHandler = (val: any) => {
    const value = { value: val, label: val };
    if (selectedTime !== value) {
      setSelectedTime(value);
      let condition = { ...conditions[index] };
      condition = { ...condition, time: value.value };
      updateNode(condition, index);
    }
  };

  const clearEmailHandler = () => {
    setSelectedEmail('');
    setSelectedLink(null);
    setSelectedLinkType(null);
    enableSaveButton(false);
    // Setting the condition to initial state
    let condition = {
      emailId: 0,
      emailType: '',
      emailName: '',
      link: '',
      linkType: '',
      sp_uuid: '',
      controlId: selectedCondition.id,
      controlName: selectedCondition.value,
    };
    updateNode(condition, index);
  };

  // Set data on Edit Node in Conditions
  const isConditionModalOpen = modal.visible === true && modal.tab === ControlNameEnum.IF_ELSE;
  let dependencies = itemData && isConditionModalOpen && emailsLinks;
  useEffect(() => {
    if (isConditionModalOpen && itemData && Object.keys(emailsLinks).length > 0) {
      let conditionObject = { ...condition };
      if (Object.keys(conditionObject).length > 0) {
        let emailId = conditionObject?.emailId;
        let indexOfEmail = emails.findIndex((email) => email?.emailId?.toString() === emailId?.toString());
        setSelectedEmailLinks(emailsLinks[emailId] || []);
        setSelectedCondition({
          id: conditionObject.controlId,
          value: conditionObject.controlName,
          label: conditionObject.controlName,
        });
        if (indexOfEmail > -1) {
          setSelectedEmail(emailId ?? conditionObject?.sp_uuid);
        }
        if (emailId && indexOfEmail > -1 ? conditionObject.link && emailsLinks[emailId] : conditionObject.link === 'Any Link') {
          if (conditionObject?.linkType) {
            let indexOf = emailsLinks[emailId]?.findIndex((item: any) =>
              item.type === 'buttons'
                ? conditionObject?.linkType === 'buttons' || conditionObject?.linkType === 'articles'
                : item.type === conditionObject?.linkType,
            );
            if (indexOf > -1) {
              let linkIndex = emailsLinks[emailId][indexOf]?.options.findIndex((item: any) => item.value === conditionObject.link);
              if (linkIndex > -1) {
                setSelectedLink(conditionObject.link);
                setSelectedLinkType(conditionObject?.linkType);
              } else {
                setSelectedLink(null);
                setSelectedLinkType(null);
                enableSaveButton(false);
              }
            } else if (conditionObject.link === 'Any Link') {
              setSelectedLink(conditionObject.link);
              setSelectedLinkType(conditionObject?.linkType);
            }
          } else {
            setSelectedLink(conditionObject.link);
            setSelectedLinkType(conditionObject?.linkType);
          }
        } else {
          setSelectedLink(null);
          setSelectedLinkType(null);
        }
      }
    } else if (isConditionModalOpen && selectedEmail !== '') {
      setSelectedEmail('');
      setSelectedCondition(conditionControls[0]);
      enableSaveButton(false);
    }
  }, [dependencies, conditions.length]);

  // Set data on Edit Node in Delay Conditions
  const isWaitModalOpen = modal.visible === true && modal.tab === ControlNameEnum.WAIT;
  let waitDependencies = itemData && isWaitModalOpen && emailsLinks;
  useEffect(() => {
    if (
      isWaitModalOpen &&
      waitRadioValue === 2 &&
      itemData &&
      (Object.keys(emailsLinks).length > 0 || condition.controlId === 12 || condition.controlId === 13)
    ) {
      let conditionObject = { ...condition };
      if (Object.keys(conditionObject).length > 0) {
        let emailId = conditionObject?.emailId;
        let indexOfEmail = emails.findIndex((email) => email?.emailId?.toString() === emailId?.toString());
        setSelectedEmailLinks(emailsLinks[emailId] || []);
        setSelectedCondition({
          id: conditionObject.controlId,
          value: conditionObject.controlName,
          label: conditionObject.controlName,
        });
        if (conditionObject.controlId !== 12 && conditionObject.controlId !== 13) {
          // CASE: has opened/has not opened & has clicked/has not clicked
          if (indexOfEmail > -1) {
            // if index exists
            setSelectedEmail(emailId ?? conditionObject?.sp_uuid);
          }
          if (emailId && indexOfEmail > -1 ? conditionObject.link && emailsLinks[emailId] : conditionObject.link === 'Any Link') {
            if (conditionObject?.linkType) {
              let indexOf = emailsLinks[emailId]?.findIndex((item: any) =>
                item.type === 'buttons'
                  ? conditionObject?.linkType === 'buttons' || conditionObject?.linkType === 'articles'
                  : item.type === conditionObject?.linkType,
              );
              if (indexOf > -1) {
                let linkIndex = emailsLinks[emailId][indexOf]?.options.findIndex((item: any) => item.value === conditionObject.link);
                if (linkIndex > -1) {
                  setSelectedLink(conditionObject.link);
                  setSelectedLinkType(conditionObject?.linkType);
                } else {
                  setSelectedLink(null);
                  setSelectedLinkType(null);
                  enableSaveButton(false);
                }
              } else if (conditionObject.link === 'Any Link') {
                setSelectedLink(conditionObject.link);
                setSelectedLinkType(conditionObject?.linkType);
              }
            } else {
              setSelectedLink(conditionObject.link);
              setSelectedLinkType(conditionObject?.linkType);
            }
          } else {
            setSelectedLink(null);
            setSelectedLinkType(null);
          }
        } else {
          // CASE: current day & current time
          conditionObject.controlId === 12 && setSelectedDay({ value: conditionObject.day, label: conditionObject.day });
          conditionObject.controlId === 13 && setSelectedTime({ value: conditionObject.time, label: conditionObject.time });
          setSelectedTimezone({ value: `My Timezone (${conditionObject.timezone})`, label: `My Timezone (${conditionObject.timezone})` });
          setSelectedVerb({ value: conditionObject.verb, label: conditionObject.verb });
        }
      }
    } else if (isWaitModalOpen && waitRadioValue === 2 && selectedEmail !== '') {
      setSelectedEmail('');
      setSelectedCondition(conditionControls[0]);
      enableSaveButton(false);
    }
  }, [waitDependencies, conditions.length]);

  useEffect(() => {
    if (modal.visible === false && selectedEmail !== '') {
      setSelectedEmail('');
      setSelectedLink(null);
      setSelectedLinkType(null);
      setSelectedCondition(conditionControls[0]);
    }
  }, [modal]);

  useEffect(() => {
    if (selectedCondition && selectedEmail) {
      setSelectedIfElsesAction(parseInt(selectedCondition.id.toString()), parseInt(selectedEmail));
    } else {
      setSelectedIfElsesAction(0, 0);
    }
  }, [selectedCondition, selectedEmail]);

  useEffect(() => {
    if (!actionModalVisible) {
      setSelectedIfElsesAction(0, 0);
      if (selectedEmail && selectedEmail?.length > 0 && selectedEmail?.includes('-')) {
        setSelectedEmail('');
      }
    }
  }, [actionModalVisible]);

  return (
    <Wrapper className="condition-selection">
      {isAdditional ? (
        <div className="condition-buttons">
          <Radio.Group
            options={operatorOptions}
            value={conditionOperators ? conditionOperators[0] : 'AND'}
            optionType="button"
            buttonStyle="solid"
            onChange={onChangeOperator}
          />
          <span className="removeBtn" onClick={() => removeCondition(index)}>
            Remove
          </span>
        </div>
      ) : null}
      <Row gutter={[32, 0]}>
        <Col span={12}>
          {/* Condition Dropdown */}
          <SelectAnt
            defaultValue={{ value: 'Select a condition', label: 'Select a condition' }}
            options={conditionControls}
            className="select-condition"
            onSelect={selectHandler}
            value={selectedCondition}
            getPopupContainer={() => document.querySelectorAll('.select-condition')[0] as HTMLElement}
            showTick
          />
        </Col>
        {selectedCondition.value !== ConditionEnum.CURRENT_DAY && selectedCondition.value !== ConditionEnum.CURRENT_TIME ? (
          // Email Dropdown
          <Col span={12}>
            <SelectAnt
              onChange={onEmailChange}
              value={
                emails.find(
                  (email) =>
                    (email?.sp_uuid && email?.sp_uuid?.toString() === selectedEmail?.toString()) ||
                    (email?.emailId && email?.emailId?.toString() == selectedEmail?.toString()),
                )?.title
              }
              showSearch
              placeholder="Select email"
              className="condition-dropdown email-condition"
              optionFilterProp="children"
              allowClear
              filterOption={(input: string, option: DefaultOptionType) =>
                (option!.children as unknown as string).toLowerCase().includes(input.toLowerCase())
              }
              onClear={clearEmailHandler}
              defaultActiveFirstOption={false}
              getPopupContainer={() => document.querySelectorAll('.email-condition')[0] as HTMLElement}
            >
              {emails?.map((email: ConditionEmails, index) => {
                let eId = `${!email.emailId ? email.sp_uuid : email.emailId}`;
                const isSelectedOption = email?.emailId?.toString() == selectedEmail;
                if (email.emailId || email.sp_uuid) {
                  return (
                    <Option
                      style={{ color: `${isSelectedOption && '#27ce0a'}` }}
                      className={`${isSelectedOption && 'email-option'}`}
                      key={index}
                      value={eId}
                    >
                      <Tick style={{ opacity: `${isSelectedOption ? '1' : '0'}`, marginRight: '8px' }} />
                      {email.title}
                    </Option>
                  );
                }
              })}
            </SelectAnt>
          </Col>
        ) : (
          <>
            <Col span={12}>
              {/* Timezone Dropdown */}
              <SelectAnt defaultValue={timezones[0]} options={timezones} onChange={selectTimezoneHandler} value={selectedTimezone} showTick />
            </Col>
            {selectedCondition.value === ConditionEnum.CURRENT_TIME || selectedCondition.value === ConditionEnum.CURRENT_DAY ? (
              <Col span={12}>
                <SelectAnt defaultValue={{ value: 'Is', label: 'Is' }} options={verbs} onChange={selectVerbHandler} value={selectedVerb} showTick />
              </Col>
            ) : null}
            {selectedCondition.value === ConditionEnum.CURRENT_DAY ? (
              <Col span={12}>
                <SelectAnt
                  defaultValue={{ value: 'Select day', label: 'Select day' }}
                  options={days}
                  onChange={selectDayHandler}
                  value={selectedDay}
                  showTick
                />
              </Col>
            ) : (
              <Col span={12} className="time-dropdown">
                <SelectAnt
                  defaultValue={{ value: 'Select time', label: 'Select time' }}
                  options={times}
                  onChange={selectTimeHandler}
                  value={selectedTime}
                  showTick
                />
              </Col>
            )}
          </>
        )}
        {(selectedCondition.value === ConditionEnum.HAS_CLICKED || selectedCondition.value === ConditionEnum.HAS_NOT_CLICKED) && selectedEmail && (
          <Col span={12}>
            <SelectAnt
              className="condition-dropdown link-condition"
              showSearch={false}
              placeholder="Please select links"
              onChange={onLinkChange}
              allowClear
              value={selectedLinkType && selectedLink ? `${selectedLinkType}#-#${selectedLink}` : null}
              filterOption={(input: string, option: DefaultOptionType) =>
                (option!.value as unknown as string).toLowerCase().includes(input.toLowerCase())
              }
              onClear={() => {
                setSelectedLink(null);
                setSelectedLinkType(null);
                enableSaveButton(false);
              }}
              getPopupContainer={() => document.querySelectorAll('.link-condition')[0] as HTMLElement}
            >
              {[{ value: 'Any Link', label: 'Any Link' }, ...(selectedEmailLinks ? selectedEmailLinks : [])].map((item: any) => {
                let obj = item.options ? (
                  item.options.length > 0 ? (
                    <OptGroup label={item.label}>
                      {item?.options?.map((option: any) => {
                        let popoverText =
                          option.label == option.value ? (
                            option.label
                          ) : (
                            <>
                              <div>{option.label}</div>
                              <div>{option.value}</div>
                            </>
                          );
                        let value = `${option.type}#-#${option.value}`;
                        let readMoreText = option.type === 'articles' ? 'Read More' : option.type === 'buttons' ? option.label : '';
                        let labelText = option.type === 'articles' ? `: Article (${option.label})` : option.type === 'buttons' ? '' : option.label;
                        return (
                          <Option className="text-underlined" value={value}>
                            <Tooltip placement="bottom" title={popoverText}>
                              <p className="read-more">{readMoreText}</p>
                              {labelText.length > 0 ? (
                                <Paragraph className="camp-name" ellipsis={true}>
                                  {labelText}
                                </Paragraph>
                              ) : null}
                            </Tooltip>
                          </Option>
                        );
                      })}
                    </OptGroup>
                  ) : null
                ) : (
                  <Option className={item.label === 'Any Link' ? 'any-link' : 'text-underlined'} value={`Any Link#-#${item.value}`}>
                    {item.label}
                  </Option>
                );
                return obj;
              })}
            </SelectAnt>
          </Col>
        )}
      </Row>
    </Wrapper>
  );
};

export default ConditionSelection;

const Wrapper = styled.div`
  margin-bottom: 8px;
  .ant-select-item-option-content {
    color: #7e7e7e;
  }
  .ant-select-item-group {
    padding-left: 10px;
  }
  .email-option {
    .ant-select-item-option-content {
      color: #27ce0a !important;
    }
  }
  .link-condition {
    .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
      background-color: #f0fff2 !important;
      font-weight: 600 !important;
    }
  }
  .ant-row > .ant-col > div > div {
    z-index: unset;
  }
  .ant-row {
    .ant-col {
      .ant-select {
        .ant-select-arrow {
          height: 28px;
          top: 32%;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            height: 15px;
            top: 45%;
          }
        }
      }
    }
  }
  .condition-buttons {
    margin-bottom: 8px;
    display: flex;
    align-items: center;
  }
  div {
    .ant-radio-group {
      .ant-radio-button-wrapper {
        height: 28px;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          height: 26px;
        }
      }
    }
  }
`;
